import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import ApplyWeddingTheme from "@/components/ApplyWeddingTheme";
import { Sparkles, MapPin, Calendar, Heart, Image as ImageIcon, ArrowRight } from "lucide-react";

export default function WeddingGuest() {
  const { slug } = useParams();
  const [w, setW] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    api.get(`/public/weddings/${slug}`).then(r => setW(r.data)).catch(() => setErr("not_found"));
  }, [slug]);

  if (err) return <div className="min-h-screen grid place-items-center px-4 text-center">
    <div className="glass p-10 max-w-md">
      <div className="font-serif text-3xl mb-2">Wedding not found</div>
      <p className="text-neutral-600">This link may have changed. Please check with the couple.</p>
      <Link to="/" className="btn-ghost-glass rounded-full px-5 py-2 text-sm mt-4 inline-block">Go home</Link>
    </div>
  </div>;
  if (!w) return <div className="min-h-screen grid place-items-center text-neutral-500">Loading...</div>;

  const accent = w.theme?.accent_color || "#C7A76C";

  return (
    <div className="min-h-screen relative" data-testid="guest-page">
      <ApplyWeddingTheme theme={w.theme} />
      {/* Hero */}
      <div className="relative min-h-[80vh] flex items-center justify-center px-4 py-16">
        {w.cover_image && (
          <div className="absolute inset-0 -z-10">
            <img src={w.cover_image} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-white/30 to-white/70" />
          </div>
        )}
        <div className="max-w-2xl w-full text-center">
          <div className="inline-flex items-center gap-2 glass-subtle px-4 py-2 mb-6">
            <Sparkles className="w-4 h-4" style={{ color: accent }} />
            <span className="text-[10px] uppercase tracking-[0.25em] text-neutral-600">You are invited</span>
          </div>
          <div className="font-script text-6xl sm:text-7xl mb-2" style={{ color: accent }} data-testid="guest-monogram">{w.monogram}</div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] text-neutral-900 whitespace-pre-line" data-testid="guest-couple-names">
            {w.display_name}
          </h1>
          <div className="mt-4 inline-flex items-center gap-3 text-neutral-700 text-sm glass-subtle px-4 py-2">
            {w.date && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {w.date}{w.time?` · ${w.time}`:""}</span>}
            {w.venue_name && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {w.venue_name}{w.city?`, ${w.city}`:""}</span>}
          </div>
          <p className="mt-8 text-neutral-700 max-w-lg mx-auto text-base sm:text-lg italic font-serif" data-testid="guest-welcome">
            "{w.welcome_message}"
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to={`/w/${slug}/upload`} data-testid="guest-upload-cta" className="rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 text-white shadow-lg" style={{ background: accent, boxShadow: `0 10px 30px -10px ${accent}80` }}>
              {w.upload_button_text} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to={`/w/${slug}/memories`} data-testid="guest-gallery-cta" className="btn-ghost-glass rounded-full px-6 py-3 text-sm inline-flex items-center gap-2">
              {w.gallery_button_text}
            </Link>
          </div>
        </div>
      </div>

      {/* Events */}
      {w.events && w.events.length > 0 && (
        <section className="max-w-3xl mx-auto px-4 pb-16">
          <div className="grid sm:grid-cols-2 gap-3">
            {w.events.map((ev, i) => (
              <div key={i} className="glass p-6 gloss-highlight" data-testid={`event-${i}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500">{ev.name}</div>
                <div className="font-serif text-2xl mt-1">{ev.date} {ev.time && <span className="text-neutral-500 text-lg">· {ev.time}</span>}</div>
                {ev.venue && <div className="text-neutral-600 text-sm mt-1">{ev.venue}</div>}
                {ev.description && <div className="text-neutral-500 text-xs mt-1">{ev.description}</div>}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Description */}
      {w.description && (
        <section className="max-w-2xl mx-auto px-4 pb-16 text-center">
          <div className="glass p-8">
            <div className="font-serif text-2xl">A little about us</div>
            <p className="text-neutral-600 mt-2">{w.description}</p>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="max-w-3xl mx-auto px-4 pb-24">
        <div className="glass p-8 sm:p-10 text-center gloss-highlight">
          <Heart className="w-6 h-6 mx-auto mb-2" style={{ color: accent }} />
          <div className="font-serif text-3xl">Every guest. Every wish.</div>
          <div className="text-neutral-600 text-sm mt-2 max-w-md mx-auto">Add your photos and a personal message — become part of the memories.</div>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Link to={`/w/${slug}/upload`} className="rounded-full px-6 py-3 text-sm inline-flex items-center gap-2 text-white" style={{ background: accent }} data-testid="guest-upload-cta-2">
              <ImageIcon className="w-4 h-4" /> {w.upload_button_text}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
